import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import Input from '../Input';
import Select from '../Select';
import { apiClient } from '../../lib/api';

interface ContentSource {
  id: string;
  type: 'blog' | 'product' | 'service' | 'other';
  url: string;
  title: string | null;
  isActive: boolean;
}

interface LinkedContent {
  id: string;
  taxonomyId: string;
  contentSourceId: string;
  relevanceScore: number;
  createdAt: string;
  contentSource?: ContentSource;
}

interface ContentLinkPanelProps {
  nodeId: string;
  nodeName: string;
  linkedContentCount?: number;
  onContentLinked: () => void;
}

export default function ContentLinkPanel({
  nodeId,
  nodeName,
  linkedContentCount = 0,
  onContentLinked,
}: ContentLinkPanelProps) {
  const { t } = useTranslation();
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkedContent, setLinkedContent] = useState<LinkedContent[]>([]);
  const [availableContent, setAvailableContent] = useState<ContentSource[]>([]);
  const [selectedContentIds, setSelectedContentIds] = useState<Set<string>>(new Set());
  const [newContentUrl, setNewContentUrl] = useState('');
  const [newContentType, setNewContentType] = useState<'blog' | 'product' | 'service' | 'other'>('blog');
  const [_isLoading, setIsLoading] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState('');

  // Fetch linked content
  const fetchLinkedContent = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/taxonomy/${nodeId}/content`);
      setLinkedContent(response.data.content || []);
    } catch (err: any) {
      console.error('Failed to fetch linked content:', err);
      setError('Failed to load linked content');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch available content sources
  const fetchAvailableContent = async () => {
    try {
      const response = await apiClient.get('/content-sources', {
        params: { isActive: true },
      });
      setAvailableContent(response.data.data || []);
    } catch (err: any) {
      console.error('Failed to fetch content sources:', err);
    }
  };

  useEffect(() => {
    if (showLinkModal) {
      fetchLinkedContent();
      fetchAvailableContent();
    }
  }, [showLinkModal, nodeId]);

  const handleToggleSelection = (contentId: string) => {
    const newSelection = new Set(selectedContentIds);
    if (newSelection.has(contentId)) {
      newSelection.delete(contentId);
    } else {
      newSelection.add(contentId);
    }
    setSelectedContentIds(newSelection);
  };

  const handleLinkExisting = async () => {
    if (selectedContentIds.size === 0) {
      setError('Please select at least one content source to link');
      return;
    }

    setIsLinking(true);
    setError('');

    try {
      // Link each selected content source
      const linkPromises = Array.from(selectedContentIds).map((contentId) =>
        apiClient.put(`/taxonomy/${nodeId}/content/${contentId}`, {
          relevance: 0.8,
        })
      );

      await Promise.all(linkPromises);

      // Reset and close
      setSelectedContentIds(new Set());

      // Notify parent
      onContentLinked();
      fetchLinkedContent();
      fetchAvailableContent();
    } catch (err: any) {
      console.error('Failed to link content:', err);
      setError(err.response?.data?.message || 'Failed to link content');
    } finally {
      setIsLinking(false);
    }
  };

  const handleAddNewContent = async () => {
    if (!newContentUrl.trim()) {
      setError('Please enter a valid URL');
      return;
    }

    setIsLinking(true);
    setError('');

    try {
      // Add new content source and link to node
      await apiClient.post(`/taxonomy/${nodeId}/content`, {
        url: newContentUrl.trim(),
        type: newContentType,
        generateEmbedding: true,
        extractKeywords: true,
      });

      // Reset form
      setNewContentUrl('');
      setNewContentType('blog');

      // Notify parent
      onContentLinked();
      fetchLinkedContent();
      fetchAvailableContent();
    } catch (err: any) {
      console.error('Failed to add content:', err);
      setError(err.response?.data?.message || 'Failed to add content source');
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlinkContent = async (linkId: string) => {
    try {
      const link = linkedContent.find((l) => l.id === linkId);
      if (!link) return;

      await apiClient.delete(`/taxonomy/${nodeId}/content/${link.contentSourceId}`);

      // Refresh
      fetchLinkedContent();
      fetchAvailableContent();
      onContentLinked();
    } catch (err: any) {
      console.error('Failed to unlink content:', err);
      setError(err.response?.data?.message || 'Failed to unlink content');
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'blog':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'product':
        return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'service':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200';
    }
  };

  // Filter out already linked content from available list
  const linkedContentIds = new Set(linkedContent.map((lc) => lc.contentSourceId));
  const unlinkedContent = availableContent.filter((c) => !linkedContentIds.has(c.id));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h3 className="text-lg font-bold dark:text-white">
          {t('settings.taxonomy.linkedContent', 'Linked Content')}
        </h3>
        <Button variant="secondary" onClick={() => setShowLinkModal(true)}>
          + {t('settings.taxonomy.linkContent', 'Link')}
        </Button>
      </div>

      {/* Linked Content Summary */}
      {linkedContentCount > 0 ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('settings.taxonomy.contentLinksCount', '{{count}} content sources linked', {
            count: linkedContentCount,
          })}
        </p>
      ) : (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('settings.taxonomy.noLinkedContent', 'No content linked yet. Link content sources to this category.')}
        </p>
      )}

      {/* Link Content Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 border-2 border-black dark:border-white rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4 dark:text-white">
              {t('settings.taxonomy.linkContentModal.title', 'Link Content to {{nodeName}}', { nodeName })}
            </h2>

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 border-2 border-red-500 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200 text-sm">
                {error}
              </div>
            )}

            {/* Add New Content Form */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-gray-200 dark:border-gray-700 space-y-4">
              <h3 className="font-bold dark:text-white mb-3">
                {t('settings.taxonomy.addNewContent', 'Add New Content Source')}
              </h3>
              <Input
                label={t('settings.taxonomy.contentUrl', 'Content URL')}
                value={newContentUrl}
                onChange={(e) => setNewContentUrl(e.target.value)}
                placeholder="https://example.com/blog/post"
                disabled={isLinking}
              />
              <Select
                label={t('settings.taxonomy.contentType', 'Content Type')}
                value={newContentType}
                onChange={(e) => setNewContentType(e.target.value as 'blog' | 'product' | 'service' | 'other')}
                disabled={isLinking}
              >
                <option value="blog">Blog Post</option>
                <option value="product">Product Page</option>
                <option value="service">Service Page</option>
                <option value="other">Other</option>
              </Select>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t('settings.taxonomy.contentLinkNote', 'The content will be scraped and automatically linked to this category.')}
              </p>
              <Button
                onClick={handleAddNewContent}
                disabled={isLinking || !newContentUrl.trim()}
                className="w-full"
              >
                {isLinking
                  ? t('common.adding', 'Adding...')
                  : t('settings.taxonomy.addAndLink', 'Add & Link')}
              </Button>
            </div>

            {/* Link Existing Content */}
            {unlinkedContent.length > 0 && (
              <div className="mb-6 p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg">
                <h3 className="font-bold dark:text-white mb-3">
                  {t('settings.taxonomy.linkExisting', 'Link Existing Content')}
                </h3>
                <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                  {unlinkedContent.map((content) => (
                    <label
                      key={content.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                      <input
                        type="checkbox"
                        checked={selectedContentIds.has(content.id)}
                        onChange={() => handleToggleSelection(content.id)}
                        className="w-4 h-4"
                        disabled={isLinking}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(content.type)}`}>
                            {content.type}
                          </span>
                        </div>
                        <p className="text-sm font-medium dark:text-white truncate">
                          {content.title || content.url}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{content.url}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <Button
                  onClick={handleLinkExisting}
                  disabled={isLinking || selectedContentIds.size === 0}
                  className="w-full"
                >
                  {isLinking
                    ? t('common.linking', 'Linking...')
                    : t('settings.taxonomy.linkSelected', 'Link {{count}} Selected', {
                        count: selectedContentIds.size,
                      })}
                </Button>
              </div>
            )}

            {/* Currently Linked */}
            {linkedContent.length > 0 && (
              <div className="mb-6">
                <h3 className="font-bold mb-2 dark:text-white">
                  {t('settings.taxonomy.currentlyLinked', 'Currently Linked')} ({linkedContent.length})
                </h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {linkedContent.map((link) => (
                    <div
                      key={link.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(link.contentSource?.type || 'other')}`}>
                            {link.contentSource?.type || 'other'}
                          </span>
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {Math.round(link.relevanceScore * 100)}% relevance
                          </span>
                        </div>
                        <p className="text-sm dark:text-white truncate">
                          {link.contentSource?.title || link.contentSource?.url || 'Unknown'}
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        onClick={() => handleUnlinkContent(link.id)}
                        className="ml-2 flex-shrink-0"
                      >
                        {t('common.unlink', 'Unlink')}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowLinkModal(false);
                  setSelectedContentIds(new Set());
                  setNewContentUrl('');
                  setNewContentType('blog');
                  setError('');
                }}
                disabled={isLinking}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
